import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PanelChart from './PanelChart';
import styles from './MonitoringDashboardModal.module.css';

// PanelChart 의 leftWidth/rightWidth 자동 계산 규칙(label 있으면 58, 없으면 45)
// 과 동일한 로직 — 병합 카드 안에서 sub-panel 들의 plot area 좌/우 edge 를 정렬
// 하기 위해 max 값을 미리 산출해 PanelChart 에 강제 width 로 넘김.
function leftAxisWidthOf(sub) {
  const opts = sub?.yAxis || {};
  const showTitle = opts.showTitle !== false;
  return (showTitle && opts.title) ? 58 : 45;
}
function rightAxisWidthOf(sub) {
  // 우측 축 실제 사용 여부 — yFieldsConfig 에서 axis='right' 인 yField 가 있어야 함
  const yFields = Array.isArray(sub?.yFields) ? sub.yFields : [];
  const cfg = sub?.yFieldsConfig || {};
  const hasRight = yFields.some(f => cfg[f]?.axis === 'right');
  if (!hasRight) return 0;
  const opts = sub?.yAxisRight || {};
  const showTitle = opts.showTitle !== false;
  return (showTitle && opts.title) ? 58 : 45;
}

/**
 * 병합 차트 카드 — chartType='merged' 패널의 본문 렌더러.
 *
 * `panel.subPanelIds` 의 panelId 들을 같은 탭의 panel 정의에서 lookup,
 * 각각을 작은 PanelChart 로 세로 stack. 맨 아래 sub-panel 만 X축 라벨 표시.
 *
 * 자체 fetch 안 함 — sub-panel 들은 같은 탭의 일반 panel 이라 PanelTabContent
 * 의 fetch 루프가 이미 panelData 에 채워둠. 여기는 그걸 lookup 만.
 *
 * v1 정책:
 * - sub-panel 클릭으로 변수 발행(publishVar) 비활성. publishVar 가 필요한
 *   패널은 자기 슬롯에 별도 배치해 사용. (UI 모델 일관성)
 * - sub-panel 의 enabled=false 또는 삭제는 graceful — 빈 chart 또는 skip.
 */
const MergedPanelView = ({ panel, panelById, panelData, isDarkMode, isFullscreen, timeWindowMin, timeDomain, displayMode }) => {
  const { t } = useTranslation();
  const ids = Array.isArray(panel?.subPanelIds) ? panel.subPanelIds : [];
  // dangling reference (삭제된 panel) 는 filter 로 제거.
  const subPanels = ids.map(id => panelById?.get?.(id)).filter(Boolean);

  // X축 수직 정렬을 위한 sub-panel 공통 plot area 폭 계산.
  // 좌측: sub-panel 들의 leftAxisWidth 의 max → 모두 그 값으로 강제.
  // 우측: sub-panel 들의 rightAxisWidth 의 max. 이 값이 0보다 크면 우측 축
  //   안 쓰는 sub-panel 들도 그만큼 reservedRight 으로 plot area 우측 edge 정렬.
  const { maxLeft, maxRight } = useMemo(() => {
    if (subPanels.length === 0) return { maxLeft: 0, maxRight: 0 };
    let l = 0, r = 0;
    for (const s of subPanels) {
      l = Math.max(l, leftAxisWidthOf(s));
      r = Math.max(r, rightAxisWidthOf(s));
    }
    return { maxLeft: l, maxRight: r };
  }, [subPanels]);

  if (subPanels.length === 0) {
    return (
      <div className={styles.mergedEmpty}>
        <span>{t('monitoring:mv.empty')}</span>
      </div>
    );
  }

  return (
    <div className={styles.mergedStack}>
      {subPanels.map((sub, idx) => {
        const isLast = idx === subPanels.length - 1;
        const state = panelData?.[sub.id];
        const waiting = Array.isArray(state?.waiting) ? state.waiting : null;
        return (
          <div key={sub.id} className={styles.mergedItem}>
            {!sub.hideTitle && sub.title && (
              <div className={styles.mergedSubTitle} title={sub.title}>{sub.title}</div>
            )}
            {state?.error && (
              <div className={styles.panelError}>{t('monitoring:mv.queryErrorMsg', { message: state.error })}</div>
            )}
            {waiting && waiting.length > 0 ? (
              <div className={styles.panelWaitingBody}>
                <small>{t('monitoring:mv.awaitingVars', { vars: waiting.map(v => `:${v}`).join(', ') })}</small>
              </div>
            ) : sub.enabled === false ? (
              <div className={styles.panelDisabledBody}>
                <span>{t('monitoring:mv.inactive')}</span>
              </div>
            ) : (
              <PanelChart
                panel={sub}
                data={state?.data || []}
                height="auto"
                hideXAxis={!isLast}
                isDarkMode={isDarkMode}
                isFullscreen={isFullscreen}
                timeWindowMin={timeWindowMin}
                timeDomain={timeDomain}
                displayMode={displayMode}
                forcedLeftAxisWidth={maxLeft}
                forcedRightAxisWidth={maxRight}
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

export default MergedPanelView;
